import React, { useEffect, useState } from 'react';
import { getStoredToken } from '../services/authSession';
import { Button } from './ui/button';
import { Database, RefreshCw, Calculator, AlertCircle, Settings } from 'lucide-react';
import GLCalculations from './GLCalculations';
import API from '../services/apiBase';
import { useClient } from '../context/ClientContext';

const GLCalculationsPage = () => {
  const { clientId, clientName, hasActiveClient } = useClient();
  const [recentGl, setRecentGl] = useState([]);
  const [glId, setGlId] = useState('');
  const [loading, setLoading] = useState(false);
  const [orgConfigWarning, setOrgConfigWarning] = useState(null); // null | 'missing' | 'needs_review'

  const loadRecent = async () => {
    if (!clientId) return;
    try {
      const res = await fetch(`${API}/gl/list?limit=15&tenant_id=${clientId}`);
      if (res.ok) {
        const j = await res.json();
        const glList = j.gl || [];
        setRecentGl(glList);

        const currentStillValid = glId && glList.some(item => item.gl_id === glId);
        if (!currentStillValid) {
          // Prefer larger datasets so tiny test uploads do not become default selections.
          const sortedByRows = [...glList].sort((a, b) => (b.row_count || 0) - (a.row_count || 0));
          const bestGl = sortedByRows[0];
          setGlId(bestGl?.gl_id || '');
        }
      }
    } catch (e) { console.warn('Failed to load recent GL list', e); }
  };

  const checkOrgConfig = async () => {
    if (!clientId) return;
    try {
      const token = getStoredToken();
      const res = await fetch(`${API}/intake/v2/org-config/${clientId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.status === 404) {
        setOrgConfigWarning('missing');
        return;
      }
      if (!res.ok) return;
      const config = await res.json();
      // The "Fringe Rate Not Configured" check was dropped, 2026-08-20:
      // org_config.fringe_rates isn't actually consumed by calculations
      // at all -- the rules engine computes each fringe pool's real rate
      // at runtime from the section's own pool/base rules, and the Org
      // Configuration save endpoint deliberately never writes this field
      // (intake_routes_v2.py's save_org_config: "fringe_rates is
      // intentionally excluded -- rates are computed at runtime from
      // rules"). A manually-verified config's fringe_rates is always
      // empty as a result, so this check was a permanent false positive
      // for every non-auto-detected client, not a real signal of
      // anything the wizard would have caught earlier if it were wrong.
      //
      // Auto-detected configs are self-healed from the GL purely to keep
      // calculations unblocked, so the "needs review" banner doesn't
      // apply to them either. Per product decision we surface NO banner
      // for them (the auto_detected / needs_review flags stay on the doc
      // for anyone reviewing via the Org Configuration UI).
      if (config.auto_detected) {
        setOrgConfigWarning(null);
      } else if (config.needs_review) {
        setOrgConfigWarning('needs_review');
      } else {
        setOrgConfigWarning(null);
      }
    } catch (e) {
      console.warn('Failed to check org config', e);
    }
  };

  useEffect(() => { loadRecent(); checkOrgConfig(); }, [clientId]);

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* No Client Selected Warning */}
      {!hasActiveClient() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600" />
          <div>
            <p className="font-medium text-yellow-800">No Client Selected</p>
            <p className="text-sm text-yellow-700">Please select a client from the sidebar to run calculations.</p>
          </div>
        </div>
      )}

      {/* Active Client Banner */}
      {hasActiveClient() && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
          <Calculator className="w-4 h-4 text-blue-600" />
          <span className="text-sm text-blue-800">
            Running calculations for: <strong>{clientName}</strong>
          </span>
        </div>
      )}

      {/* Org Config Warning Banner */}
      {hasActiveClient() && orgConfigWarning === 'missing' && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-red-800">Org Configuration Missing — Calculations Will Fail</p>
            <p className="text-sm text-red-700 mt-1">
              No org configuration has been set for <strong>{clientName}</strong>. GL calculations require
              org codes, account codes, and fringe rates to be configured. Without this, calculations
              will return an error instead of results.
            </p>
            <p className="text-sm text-red-700 mt-1">
              Go to <strong>Client Onboarding → Org Configuration</strong> to detect and save the configuration.
            </p>
          </div>
          <Settings className="w-5 h-5 text-red-500 flex-shrink-0" />
        </div>
      )}

      {hasActiveClient() && orgConfigWarning === 'needs_review' && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-yellow-800">Org Configuration Needs Review</p>
            <p className="text-sm text-yellow-700 mt-1">
              Org codes for <strong>{clientName}</strong> were auto-detected from the GL file but have not
              been reviewed. Heuristic detection may assign incorrect division mappings. Verify the
              configuration before relying on calculation results.
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              Go to <strong>Client Onboarding → Org Configuration</strong> to review and confirm.
            </p>
          </div>
          <Settings className="w-5 h-5 text-yellow-500 flex-shrink-0" />
        </div>
      )}

      {/* Page Header */}
      <div className="reconcile-header" style={{ marginBottom: '2rem', padding: '1.5rem 0', borderBottom: '1px solid var(--gray-200)' }}>
        <div className="reconcile-title">ICS Schedule Calculations</div>
        <div className="reconcile-meta">
          <div className="reconcile-meta-item">
            <Calculator style={{ width: '1rem', height: '1rem' }} />
            <span className="reconcile-meta-label">Module:</span>
            <span>Rules Engine</span>
          </div>
          <div className="reconcile-meta-item">
            <span className="reconcile-meta-label">Status:</span>
            <span className="pro-badge pro-badge-info">Ready</span>
          </div>
        </div>
      </div>

      {/* GL Selection Card */}
      <div className="pro-card" style={{ marginBottom: '2rem' }}>
        <div className="pro-card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="pro-card-title">
              <Database style={{ width: '1.25rem', height: '1.25rem', display: 'inline', marginRight: '0.5rem' }} />
              Select GL Dataset
            </h3>
            <button 
              className="pro-btn pro-btn-secondary" 
              style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }}
              onClick={loadRecent} 
              disabled={loading}
            >
              <RefreshCw style={{ width: '1rem', height: '1rem', marginRight: '0.5rem' }} />
              Refresh List
            </button>
          </div>
        </div>
        <div className="pro-card-content">
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
              GL Dataset:
            </label>
            <select 
              value={glId} 
              onChange={e => setGlId(e.target.value)} 
              className="pro-input"
              style={{ maxWidth: '500px' }}
            >
              <option value="">-- Choose uploaded GL --</option>
              {(recentGl||[]).map((g, i) => (
                <option key={i} value={g.gl_id}>
                  {(g.filename || g.sheet_name || 'GL')} • {g.gl_id} • {g.row_count?.toLocaleString() || 0} rows
                </option>
              ))}
            </select>
          </div>
          {!glId && (
            <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.5rem' }}>
              Upload a GL in the GL tab first
            </p>
          )}
        </div>
      </div>

      {/* Calculations Component */}
      {glId ? (
        <GLCalculations glId={glId} />
      ) : (
        <div className="pro-card">
          <div className="pro-card-content" style={{ textAlign: 'center', padding: '3rem' }}>
            <Calculator style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', color: 'var(--gray-300)' }} />
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
              No GL selected yet. Please upload/select a GL dataset to run calculations.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GLCalculationsPage;
